import {
  formatPrice,
  formatCurrencyAmount,
  isAmbiguousDollarCode,
  formatChargeDisclosure as baseFormatChargeDisclosure,
} from "@workspace/api-client-react";
import {
  resolveTripCurrency,
  resolveHostBookingCurrency,
  resolveHostBookingCurrencyStrict,
  resolveDisplayCurrency,
  resolveDisplayCurrencyStrict,
  type HostBookingCurrencyShape,
} from "@workspace/booking-rules";
import { useCallback, useMemo } from "react";
import { useLocale } from "@/i18n/LocaleContext";
import { marketToFormatLocale } from "@/i18n/markets";

/**
 * Task #2409 — strict variant of `resolveTripCurrency` for surfaces
 * (the trip-adjustment proposal banner et al.) where silently
 * defaulting to USD when both currency columns are empty would render
 * a `$` symbol on a non-USD booking. Throws when the trip carries
 * neither `billing_currency_code` nor `local_currency_code` instead
 * of falling through to the default. Use this anywhere a single
 * money string would be misleading without a known currency.
 */
export class UnresolvedBookingCurrencyError extends Error {
  readonly code = "UNRESOLVED_BOOKING_CURRENCY";
  constructor() {
    super(
      "Booking currency could not be resolved: trip is missing both billing_currency_code and local_currency_code.",
    );
    this.name = "UnresolvedBookingCurrencyError";
  }
}

export function resolveBookingCurrencyStrict(
  trip: { billing_currency_code?: string | null; local_currency_code?: string | null } | null | undefined,
): string {
  const billing = (trip?.billing_currency_code || "").trim();
  const local = (trip?.local_currency_code || "").trim();
  if (!billing && !local) {
    throw new UnresolvedBookingCurrencyError();
  }
  // Reuse the canonical resolver so unknown-code handling and the
  // billing → local fallback rules stay in one place.
  return resolveTripCurrency(trip);
}

/**
 * Task #5738 — host-facing currency for a booking. Renters are charged
 * in `billing_currency_code` (billing-first `resolveTripCurrency`), but
 * a host thinks in — and is paid in — the car's LOCAL market currency.
 * A ZAR car booked with a cross-border partial payment carries
 * `billing_currency_code = "USD"`, so billing-first mislabels the host's
 * ZAR amounts as USD. This adapter maps a web trip onto the shared
 * market-first `resolveHostBookingCurrency` (local → country → car →
 * billing → USD). Use ONLY on host surfaces; renter surfaces keep
 * `resolveTripCurrency` / `resolveBookingCurrencyStrict`.
 */
type HostTripLike =
  | ({
      billing_currency_code?: string | null;
      local_currency_code?: string | null;
      cars?: { country_code?: string | null; currency_code?: string | null; [key: string]: unknown } | null;
    })
  | null
  | undefined;

function toHostShape(trip: HostTripLike): HostBookingCurrencyShape {
  return {
    billing_currency_code: trip?.billing_currency_code ?? null,
    local_currency_code: trip?.local_currency_code ?? null,
    country_code: trip?.cars?.country_code ?? null,
    car_currency_code: trip?.cars?.currency_code ?? null,
  };
}

export function resolveHostTripCurrency(trip: HostTripLike): string {
  return resolveHostBookingCurrency(toHostShape(trip));
}

/**
 * Strict host resolver — throws when no currency signal exists at all,
 * for host surfaces (e.g. "Your earnings") where a silent `$` fallback
 * would be misleading. Mirrors `resolveBookingCurrencyStrict`.
 */
export function resolveHostTripCurrencyStrict(trip: HostTripLike): string {
  return resolveHostBookingCurrencyStrict(toHostShape(trip));
}

/**
 * Task #5745 — renter *display* currency for a booking's price
 * breakdown. Renters are CHARGED in `billing_currency_code` (settlement,
 * billing-first `resolveTripCurrency`), but every itemised breakdown row
 * (rental fee, delivery, service fee, totals, "Paid now", "Total due to
 * owner") is stored in the car's LOCAL market currency. A New Zealand car
 * booked cross-border carries `billing_currency_code = "USD"` while the
 * line items are NZD, so billing-first mislabels those NZD numbers as USD.
 *
 * This adapter maps a web trip onto the shared market-first
 * `resolveDisplayCurrency` (local → country → car → billing → USD) — the
 * same ordering as the host resolver, because the breakdown numbers are
 * denominated the same way. Use ONLY on renter breakdown / quote
 * surfaces. Settlement surfaces (the Payments/transactions list,
 * cancellation/refund math) keep `resolveTripCurrency` /
 * `resolveBookingCurrencyStrict`. Label change only — never a
 * re-conversion of the amounts.
 */
export function resolveDisplayTripCurrency(trip: HostTripLike): string {
  return resolveDisplayCurrency(toHostShape(trip));
}

/**
 * Strict renter-display resolver — throws when no currency signal exists
 * at all, for breakdown surfaces where a silent `$` fallback would be
 * misleading. Mirrors `resolveBookingCurrencyStrict`.
 */
export function resolveDisplayTripCurrencyStrict(trip: HostTripLike): string {
  return resolveDisplayCurrencyStrict(toHostShape(trip));
}

/**
 * Numeric formatter (no symbol) — kept for legacy callers that compose
 * the symbol themselves. Prefer `formatMoneyWithCurrency` /
 * `formatPriceCode` for new code so the ambiguous-symbol code suffix
 * (task #2342) stays consistent.
 *
 * Task #2444 — optional `locale` so callers in market-aware surfaces
 * pick up Danish / German / Swedish number conventions; defaults to
 * `en-GB` to preserve historical behaviour for non-aware callers.
 */
export function formatMoney(
  value: number,
  opts?: { decimals?: number; locale?: string },
): string {
  const decimals = opts?.decimals ?? 2;
  const locale = opts?.locale || "en-GB";
  if (!Number.isFinite(value)) return (0).toFixed(decimals);
  return value.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Render an amount with its currency. Ambiguous-symbol currencies
 * (USD/NZD/AUD/CAD/...) get an ISO code suffix (`$120 NZD`); unambiguous
 * currencies (GBP/EUR/...) render as `£120`.
 *
 * Required (throws on empty currency) — pass the literal "USD" if none
 * is genuinely available.
 *
 * Task #2444 — optional `locale` (BCP-47) tunes the thousands /
 * decimal separators and symbol placement. Defaults to `en-GB` so
 * non-aware callers don't regress; React components should prefer
 * `useFormatMoney()` which closes over the active market locale.
 */
export function formatMoneyWithCurrency(
  value: number | null | undefined,
  currency: string,
  opts?: { decimals?: number; locale?: string },
): string {
  if (typeof currency !== "string" || currency.trim() === "") {
    throw new Error("formatMoneyWithCurrency: `currency` is required");
  }
  const code = currency.toUpperCase();
  const amount = Number(value ?? 0);
  // When the caller did not pin `decimals`, defer entirely to the
  // shared `formatPrice` heuristic (drops trailing zeros for whole
  // amounts; keeps two decimals for fractional ones).
  if (opts?.decimals == null) {
    return formatPrice(amount, code, opts?.locale) ?? "";
  }
  // Caller pinned an explicit decimal count (e.g. EarningsPage tiles
  // pass `decimals: 0` for the rolled-up totals). Honour it so the
  // surface controls rounding/suppression while keeping locale-aware
  // separators and Intl-driven symbol placement.
  return formatCurrencyAmount(amount, code, opts.locale, { decimals: opts.decimals });
}

/**
 * Alias matching the task #2342 helper name. Always returns a string
 * (callers in this app never pass null), unlike `formatPrice` which is
 * nullable.
 */
export function formatPriceCode(amount: number, currency: string, locale?: string): string {
  return formatPrice(amount, currency.toUpperCase(), locale) ?? "";
}

export { isAmbiguousDollarCode };

/**
 * Task #2342 — produces the dual-currency disclosure used at checkout
 * when the renter's preferred currency differs from the actual charge
 * currency. Returns `null` when both currencies match so callers can
 * render a single line.
 */
export function formatChargeDisclosure(
  preferredAmount: number,
  preferredCurrency: string,
  chargeAmount: number,
  chargeCurrency: string,
  locale?: string,
): { preferred: string; charge: string } | null {
  const out = baseFormatChargeDisclosure(
    preferredAmount,
    preferredCurrency,
    chargeAmount,
    chargeCurrency,
    locale,
  );
  if (out.sameCurrency) return null;
  return { preferred: out.preferred, charge: out.charge };
}

/**
 * Task #2444 — hook variant for React components. Closes over the
 * active market's BCP-47 format locale (derived from the URL) so
 * prices on `/dk/...` render `24.000 kr`, on `/de/...` render
 * `24.000 €`, on `/se/...` render `24 000 kr`, and on `/uk/...` keep
 * the existing `£24,000`.
 *
 * Returns the same helper signatures as the module-level functions
 * but with the locale already bound — call sites stay terse:
 *
 *   const fmt = useFormatMoney();
 *   fmt.money(amount, currency);
 *   fmt.disclosure(preferredAmt, preferredCur, chargeAmt, chargeCur);
 */
export function useFormatMoney() {
  const { market, language } = useLocale();
  const locale = useMemo(() => marketToFormatLocale(market, language), [market, language]);

  const money = useCallback(
    (value: number | null | undefined, currency: string, opts?: { decimals?: number }) =>
      formatMoneyWithCurrency(value, currency, { ...opts, locale }),
    [locale],
  );
  const numeric = useCallback(
    (value: number, opts?: { decimals?: number }) => formatMoney(value, { ...opts, locale }),
    [locale],
  );
  const priceCode = useCallback(
    (amount: number, currency: string) => formatPriceCode(amount, currency, locale),
    [locale],
  );
  const disclosure = useCallback(
    (
      preferredAmount: number,
      preferredCurrency: string,
      chargeAmount: number,
      chargeCurrency: string,
    ) =>
      formatChargeDisclosure(
        preferredAmount,
        preferredCurrency,
        chargeAmount,
        chargeCurrency,
        locale,
      ),
    [locale],
  );

  return { locale, money, numeric, priceCode, disclosure };
}
